import { captureException } from "@utils/captureException";
import { trpc } from "@utils/trpc";
import { useQueryClient } from "@tanstack/react-query";
import {
  inboxListQueryKey,
  inboxUnreadQueryKey,
} from "@utils/realtime/invalidation";
import { useCallback } from "react";

/**
 * Hook to access Postgres-backed inbox functionality.
 * Realtime invalidates these tRPC queries while the app is foregrounded.
 */
export const useInbox = () => {
  const queryClient = useQueryClient();
  const inbox = trpc.user.inbox.listItems.useQuery();

  const invalidateInboxQuery = useCallback(
    (queryKey: ReturnType<typeof inboxListQueryKey>) => {
      queryClient.invalidateQueries({ queryKey }).catch((error) => {
        captureException(error, {
          channel: "breadcrumb",
          area: "inbox",
          operation: "invalidate-inbox-query",
        });
      });
    },
    [queryClient],
  );

  const invalidateInboxQueries = useCallback(() => {
    invalidateInboxQuery(inboxListQueryKey());
    invalidateInboxQuery(inboxUnreadQueryKey());
  }, [invalidateInboxQuery]);

  const markReadMutation = trpc.user.inbox.markAsRead.useMutation({
    onSuccess: invalidateInboxQueries,
  });
  const markAllReadMutation = trpc.user.inbox.markAllAsRead.useMutation({
    onSuccess: invalidateInboxQueries,
  });

  const markAsRead = (ids: string[]) => {
    if (ids.length === 0) return;
    markReadMutation.mutate(ids, {
      onError: (error) => {
        captureException(error, {
          channel: "breadcrumb",
          area: "inbox",
          operation: "mark-inbox-items-read",
        });
      },
    });
  };

  const markAllAsRead = () => {
    markAllReadMutation.mutate(undefined, {
      onError: (error) => {
        captureException(error, {
          channel: "breadcrumb",
          area: "inbox",
          operation: "mark-all-inbox-items-read",
        });
      },
    });
  };

  return {
    inbox: inbox.data ?? [],
    isLoadingInbox: inbox.isLoading,
    markAsRead,
    markAllAsRead,
  };
};

/**
 * Hook to check if user has unread inbox items.
 * Realtime invalidates this query when the Postgres unread token changes.
 */
export const useHasUnreadInbox = () => {
  const hasUnread = trpc.user.inbox.hasUnread.useQuery();
  return hasUnread.data ?? false;
};

/**
 * Hook to mark an inbox item as read by external message ID.
 * Used for cross-channel integrations (push/email deep link opens).
 */
export const useMarkReadByExternalMessageId = () => {
  const queryClient = useQueryClient();
  const invalidateInboxQuery = useCallback(
    (queryKey: ReturnType<typeof inboxListQueryKey>) => {
      queryClient.invalidateQueries({ queryKey }).catch((error) => {
        captureException(error, {
          channel: "breadcrumb",
          area: "inbox",
          operation: "invalidate-inbox-query",
        });
      });
    },
    [queryClient],
  );

  const markReadByExternalIdMutation =
    trpc.user.inbox.markReadByExternalMessageId.useMutation({
      onSuccess: () => {
        invalidateInboxQuery(inboxListQueryKey());
        invalidateInboxQuery(inboxUnreadQueryKey());
      },
    });

  const markReadByExternalMessageId = (externalMessageId: string) => {
    return markReadByExternalIdMutation.mutateAsync(
      { externalMessageId },
      {
        onError: (error) => {
          captureException(error, {
            channel: "breadcrumb",
            area: "inbox",
            operation: "mark-inbox-item-read-by-external-message-id",
          });
        },
      },
    );
  };

  return { markReadByExternalMessageId };
};
